<app-catalog-page-card title="INVESTOR GROUP">
  <app-catalog-table-toolbar
    datatableId="kt_investor_group_datatable"
    searchPlaceholder="Search investor group records..."
    addButtonLabel="Add Investor Group"
    addModalId="create_investor_group_modal"
    refreshAriaLabel="Refresh investor groups"
    [disableRefresh]="isLoading()"
    (refresh)="loadRows()"
  ></app-catalog-table-toolbar>

  <app-catalog-crud-modal
    modalId="create_investor_group_modal"
    title="Create Investor Group"
    submitLabel="Create Investor Group"
    [submitting]="isCreating()"
    [formError]="createFormError()"
    (submit)="onCreate()"
    (cancel)="resetCreateForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="createForm">
        @if (lookupError()) {
          <div class="kt-alert kt-alert-destructive" role="alert">
            <div class="kt-alert-text">{{ lookupError() }}</div>
          </div>
        }

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-investor-group-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="create-investor-group-name"
            type="text"
            class="kt-input"
            maxlength="60"
            formControlName="name"
            [attr.aria-invalid]="shouldShowCreateFieldError('name')"
          />

          @if (shouldShowCreateRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowCreateMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 60 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-investor-group-country">Country</label>
          <select
            id="create-investor-group-country"
            class="kt-select"
            data-kt-select="true"
            data-kt-select-enable-search="true"
            data-kt-select-search-placeholder="Search..."
            data-kt-select-placeholder="Select a country..."
            data-kt-select-config='{"optionsClass":"kt-scrollable overflow-auto max-h-[250px]"}'
            formControlName="hqCountryId"
          >
            <option value="">Select a country...</option>
            @for (item of countries(); track item.id) {
              <option [value]="item.id">{{ item.name }}</option>
            }
          </select>
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="create-investor-group-region">Region</label>
          <select id="create-investor-group-region" class="kt-select" formControlName="regionId">
            <option value="">Select region...</option>
            @for (item of regions(); track item.id) {
              <option [value]="item.id">{{ item.name }}</option>
            }
          </select>
        </div>
      </form>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#edit_investor_group_modal"
  ></button>

  <app-catalog-crud-modal
    modalId="edit_investor_group_modal"
    title="Edit Investor Group"
    submitLabel="Save Changes"
    widthClass="max-w-[1100px]"
    [submitting]="isUpdating()"
    [formError]="editFormError()"
    (submit)="onUpdate()"
    (cancel)="resetEditForm()"
  >
    <div modal-body>
      <form class="grid gap-4" autocomplete="off" [formGroup]="editForm">
        @if (lookupError()) {
          <div class="kt-alert kt-alert-destructive" role="alert">
            <div class="kt-alert-text">{{ lookupError() }}</div>
          </div>
        }

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-investor-group-name">
            Name <span class="catalog-required-marker" aria-hidden="true">*</span>
          </label>
          <input
            id="edit-investor-group-name"
            type="text"
            class="kt-input"
            maxlength="60"
            formControlName="name"
            [attr.aria-invalid]="shouldShowEditFieldError('name')"
          />

          @if (shouldShowEditRequiredError('name')) {
            <div class="catalog-field-error">Name is required.</div>
          }

          @if (shouldShowEditMaxLengthError('name')) {
            <div class="catalog-field-error">Name cannot exceed 60 characters.</div>
          }
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-investor-group-country">Country</label>
          <select
            id="edit-investor-group-country"
            class="kt-select"
            data-kt-select="true"
            data-kt-select-enable-search="true"
            data-kt-select-search-placeholder="Search..."
            data-kt-select-placeholder="Select a country..."
            data-kt-select-config='{"optionsClass":"kt-scrollable overflow-auto max-h-[250px]"}'
            formControlName="hqCountryId"
          >
            <option value="">Select a country...</option>
            @for (item of countries(); track item.id) {
              <option [value]="item.id">{{ item.name }}</option>
            }
          </select>
        </div>

        <div class="grid gap-1.5">
          <label class="kt-form-label" for="edit-investor-group-region">Region</label>
          <select id="edit-investor-group-region" class="kt-select" formControlName="regionId">
            <option value="">Select region...</option>
            @for (item of regions(); track item.id) {
              <option [value]="item.id">{{ item.name }}</option>
            }
          </select>
        </div>
      </form>

      <section class="mt-6 grid gap-3">
        <div class="flex items-center justify-between gap-2">
          <h4 class="text-sm font-semibold">Attached Documentation</h4>
          <button
            type="button"
            class="kt-btn kt-btn-sm kt-btn-outline"
            [disabled]="isMutatingDocument() || editingId() === null"
            (click)="openCreateDocumentEditor()"
          >
            <i class="ki-filled ki-plus" aria-hidden="true"></i>
            <span>Add Documentation</span>
          </button>
        </div>
        @if (isDocumentEditorVisible()) {
          <div class="kt-card p-4 border rounded-md grid gap-3">
            <div class="flex items-center justify-between gap-2">
              <h5 class="text-sm font-semibold">{{ documentModalTitle() }}</h5>
              <button type="button" class="kt-btn kt-btn-sm kt-btn-light" [disabled]="isMutatingDocument()" (click)="closeDocumentEditor()">
                Cancel
              </button>
            </div>
            @if (editingDocumentId() === null) {
              <div class="grid gap-1.5">
                <label class="kt-form-label" for="investor-group-document-files">Files</label>
                <input
                  id="investor-group-document-files"
                  type="file"
                  class="kt-input"
                  accept=".doc,.msg,.pdf,.xls"
                  multiple
                  [disabled]="isMutatingDocument()"
                  (change)="onDocumentFilesSelected($event)"
                />
                <p class="text-xs text-secondary-foreground">Allowed types: .doc, .msg, .pdf, .xls</p>
              </div>
              @if (documentUploadEntries().length > 0) {
                <div class="grid gap-1">
                  <p class="text-sm font-medium">Selected files</p>
                  <app-document-upload-list [entries]="documentUploadEntries()" />
                </div>
              }
            } @else {
              <div class="grid gap-1.5">
                <label class="kt-form-label" for="investor-group-document-filename">Filename</label>
                <input
                  id="investor-group-document-filename"
                  type="text"
                  class="kt-input"
                  [value]="documentForm().filename"
                  (input)="updateDocumentField('filename', $any($event.target).value)"
                />
              </div>
              <div class="grid gap-1.5">
                <label class="kt-form-label" for="investor-group-document-description">Description</label>
                <input
                  id="investor-group-document-description"
                  type="text"
                  class="kt-input"
                  [value]="documentForm().description"
                  (input)="updateDocumentField('description', $any($event.target).value)"
                />
              </div>
            }
            @if (documentFormError()) {
              <div class="kt-alert kt-alert-destructive" role="alert">
                <div class="kt-alert-text">{{ documentFormError() }}</div>
              </div>
            }
            <div class="flex justify-end gap-2">
              <button type="button" class="kt-btn kt-btn-light" [disabled]="isMutatingDocument()" (click)="closeDocumentEditor()">Cancel</button>
              <button type="button" class="kt-btn" [disabled]="isMutatingDocument()" (click)="addOrUpdateDocument()">
                <span class="inline-flex items-center gap-2">
                  @if (isMutatingDocument()) {
                    <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  }
                  <span>{{ documentModalSubmitLabel() }}</span>
                </span>
              </button>
            </div>
          </div>
        }
        <div class="kt-table-wrapper kt-scrollable">
          <table class="kt-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Type</th>
                <th>Filename</th>
                <th>Source</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (documents().length === 0) {
                <tr>
                  <td colspan="5" class="text-center text-secondary-foreground py-4">No documentation found.</td>
                </tr>
              } @else {
                @for (document of documents(); track trackDocument($index, document)) {
                  <tr>
                    <td>
                      <app-document-description-cell
                        [description]="document.description"
                        [documentLabel]="document.filename"
                        [disabled]="isMutatingDocument()"
                        [saving]="describingDocumentId() === document.id"
                        (descriptionChange)="updateDocumentDescription(document, $event)"
                      />
                    </td>
                    <td>{{ document.type || '-' }}</td>
                    <td>
                      <app-document-name-cell
                        [filename]="document.filename"
                        [link]="document.ezShareLink"
                        [disabled]="isMutatingDocument()"
                        [saving]="renamingDocumentId() === document.id"
                        [siblingNames]="documentFilenames()"
                        (rename)="renameDocument(document, $event)"
                      />
                    </td>
                    <td>{{ document.source || '-' }}</td>
                    <td class="text-end">
                      <span class="inline-flex gap-2">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          [disabled]="isMutatingDocument()"
                          (click)="startEditDocumentRow(document)"
                          aria-label="Edit document"
                        >
                          <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                        </button>
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          [disabled]="isMutatingDocument()"
                          (click)="requestDeleteDocumentRow(document)"
                          aria-label="Delete document"
                        >
                          @if (deletingDocumentId() === document.id) {
                            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                          } @else {
                            <i class="ki-filled ki-trash" aria-hidden="true"></i>
                          }
                        </button>
                      </span>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>

      <section class="mt-6 grid gap-3">
        <div class="flex items-center justify-between gap-2">
          <h4 class="text-sm font-semibold">Notes</h4>
          <button
            type="button"
            class="kt-btn kt-btn-sm kt-btn-outline"
            [disabled]="isMutatingNote() || editingId() === null"
            (click)="openCreateNoteEditor()"
          >
            <i class="ki-filled ki-plus" aria-hidden="true"></i>
            <span>Add Note</span>
          </button>
        </div>
        @if (isNoteEditorVisible()) {
          <div class="kt-card p-4 border rounded-md grid gap-3">
            <div class="flex items-center justify-between gap-2">
              <h5 class="text-sm font-semibold">{{ noteModalTitle() }}</h5>
              <button type="button" class="kt-btn kt-btn-sm kt-btn-light" [disabled]="isMutatingNote()" (click)="closeNoteEditor()">
                Cancel
              </button>
            </div>
            <div class="grid gap-1.5">
              <label class="kt-form-label" for="investor-group-note-text">Note</label>
              <textarea
                id="investor-group-note-text"
                class="kt-textarea"
                rows="4"
                [value]="noteForm().note"
                (input)="updateNoteField($any($event.target).value)"
              ></textarea>
            </div>
            @if (noteFormError()) {
              <div class="kt-alert kt-alert-destructive" role="alert">
                <div class="kt-alert-text">{{ noteFormError() }}</div>
              </div>
            }
            <div class="flex justify-end gap-2">
              <button type="button" class="kt-btn kt-btn-light" [disabled]="isMutatingNote()" (click)="closeNoteEditor()">Cancel</button>
              <button type="button" class="kt-btn" [disabled]="isMutatingNote()" (click)="addOrUpdateNote()">
                <span class="inline-flex items-center gap-2">
                  @if (isMutatingNote()) {
                    <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                  }
                  <span>{{ noteModalSubmitLabel() }}</span>
                </span>
              </button>
            </div>
          </div>
        }
        <div class="kt-table-wrapper kt-scrollable">
          <table class="kt-table">
            <thead>
              <tr>
                <th>Note</th>
                <th>Modified By</th>
                <th>Date Modified</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (notes().length === 0) {
                <tr>
                  <td colspan="4" class="text-center text-secondary-foreground py-4">No notes found.</td>
                </tr>
              } @else {
                @for (note of notes(); track trackNote($index, note)) {
                  <tr>
                    <td>{{ note.description || '-' }}</td>
                    <td>{{ note.modifiedBy || '-' }}</td>
                    <td>{{ formatDateTime(note.dateModified) }}</td>
                    <td class="text-end">
                      <span class="inline-flex gap-2">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          [disabled]="isMutatingNote()"
                          (click)="startEditNoteRow(note)"
                          aria-label="Edit note"
                        >
                          <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                        </button>
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          [disabled]="isMutatingNote()"
                          (click)="requestDeleteNoteRow(note)"
                          aria-label="Delete note"
                        >
                          @if (deletingNoteId() === note.id) {
                            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true"></span>
                          } @else {
                            <i class="ki-filled ki-trash" aria-hidden="true"></i>
                          }
                        </button>
                      </span>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </app-catalog-crud-modal>

  <button
    type="button"
    class="hidden"
    aria-hidden="true"
    tabindex="-1"
    data-kt-modal-toggle="#delete_investor_group_confirm_modal"
  ></button>

  <app-catalog-delete-confirm-modal
    modalId="delete_investor_group_confirm_modal"
    title="Remove Investor Group"
    message="Are you sure you want to remove this investor group?"
    confirmLabel="Remove Investor Group"
    [confirming]="deletingId() !== null"
    (confirm)="confirmDelete()"
    (cancel)="cancelDelete()"
  ></app-catalog-delete-confirm-modal>

  @if (hasAlert()) {
    <div
      class="kt-alert kt-alert-light"
      [class.kt-alert-success]="alertVariant() === 'success'"
      [class.kt-alert-destructive]="alertVariant() === 'destructive'"
      role="alert"
    >
      <div class="kt-alert-icon">
        <i class="ki-filled ki-information" aria-hidden="true"></i>
      </div>

      <div class="grow">
        <div class="kt-alert-title"><strong>{{ alertTitle() }}</strong></div>

        @if (alertMessage()) {
          <div class="kt-alert-text">{{ alertMessage() }}</div>
        }
      </div>

      <div class="kt-alert-toolbar">
        <div class="kt-alert-actions">
          <button
            type="button"
            class="kt-alert-close"
            aria-label="Dismiss alert"
            (click)="dismissAlert()"
          >
            <i class="ki-filled ki-cross" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  }

  @if (isLoading()) {
    <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading investor groups">
      <span class="catalog-loading-spinner" aria-hidden="true"></span>
      <span class="catalog-loading-text">Loading...</span>
    </div>
  }

  @if (!isLoading() && errorMessage()) {
    <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
      <span>{{ errorMessage() }}</span>
      <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadRows()">Retry</button>
    </div>
  }

  @if (!isLoading() && !errorMessage()) {
    <div
      id="kt_investor_group_datatable"
      class="kt-card-table"
      data-kt-datatable="true"
      data-kt-datatable-page-size="20"
      data-kt-datatable-state-save="true"
      data-kt-datatable-state-namespace="investor-group-datatable"
      (click)="onTableActionClick($event)"
    >
      <div class="kt-table-wrapper kt-scrollable">
        <table class="kt-table" data-kt-datatable-table="true">
          <thead>
            <tr>
              <th scope="col" data-kt-datatable-column="name">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Name</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th scope="col" data-kt-datatable-column="country">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Country</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th scope="col" data-kt-datatable-column="region">
                <span class="kt-table-col">
                  <span class="kt-table-col-label">Region</span>
                  <span class="kt-table-col-sort"></span>
                </span>
              </th>
              <th
                scope="col"
                data-kt-datatable-column="actions"
                data-kt-datatable-column-sort="false"
                class="text-end"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            @if (rows().length === 0) {
              <tr>
                <td colspan="4" class="text-center text-secondary-foreground py-6">
                  No investor groups found.
                </td>
              </tr>
            } @else {
              @for (item of rows(); track item.id) {
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ item.country || '-' }}</td>
                  <td>{{ item.region || '-' }}</td>
                  <td class="text-end">
                    <span class="inline-flex gap-2.5">
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Edit investor group"
                        data-action="edit-investor-group"
                        [attr.data-investor-group-id]="item.id"
                        [disabled]="deletingId() !== null || isUpdating()"
                      >
                        <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Remove investor group"
                        data-action="delete-investor-group"
                        [attr.data-investor-group-id]="item.id"
                        [disabled]="deletingId() !== null"
                      >
                        <i class="ki-filled ki-trash" aria-hidden="true"></i>
                      </button>
                    </span>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
        <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>

        <div class="kt-datatable-length flex items-center gap-3">
          <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
          <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
        </div>
      </div>
    </div>
  }
</app-catalog-page-card>
